import { PlatformerAssetBank, type AssetLoadReport } from './AssetBank';
import { PlatformerInput } from './PlatformerInput';
import { DEFAULT_PLATFORMER_ASSET_MANIFEST } from './data/assetManifest';
import { PLATFORMER_LEVELS } from './data/levels';
import {
  approach,
  clamp,
  moveHorizontal,
  moveVertical,
  overlaps,
  type KinematicBody,
  type SolidCollider,
} from './physics';
import {
  drawFallbackBlock,
  drawFallbackCoin,
  drawFallbackGoomba,
  drawFallbackMario,
  drawFallbackPipe,
  drawFallbackScenery,
  drawFallbackTile,
} from './rendering/FallbackPixelArt';
import { drawPixelText } from './rendering/PixelFont';
import {
  PLATFORMER_FIXED_STEP,
  PLATFORMER_SCREEN_HEIGHT,
  PLATFORMER_SCREEN_WIDTH,
  type BlockSpawn,
  type CoinSpawn,
  type EnemySpawn,
  type LevelDefinition,
  type PlatformerDiagnostics,
  type PlatformerDebugState,
  type PlatformerEngineOptions,
  type PlatformerEvent,
  type PlatformerEventType,
  type PlatformerIntent,
  type PlatformerPhase,
  type SpriteAssetId,
  type SpriteAssetManifest,
  type TileKind,
} from './types';

const TILE = 16;
const MAX_FRAME_DELTA = 0.1;
const MAX_STEPS_PER_UPDATE = 8;
const BOOT_DURATION = 1.55;
const PLAYER_WIDTH = 14;
const PLAYER_HEIGHT = 16;
const PLAYER_GRAVITY = 700;
const PLAYER_MAX_FALL_SPEED = 330;
const PLAYER_WALK_SPEED = 72;
const PLAYER_RUN_SPEED = 116;
const PLAYER_JUMP_SPEED = 246;
const PLAYER_RUN_JUMP_SPEED = 274;
const PLAYER_GROUND_ACCELERATION = 820;
const PLAYER_RUN_ACCELERATION = 1060;
const PLAYER_AIR_ACCELERATION = 510;
const PLAYER_GROUND_DECELERATION = 900;
const PLAYER_AIR_DECELERATION = 55;
const PLAYER_COYOTE_TIME = 0.085;
const PLAYER_JUMP_BUFFER_TIME = 0.11;

type PlayerAnimation = 'idle' | 'run' | 'jump' | 'fall' | 'death';

interface PlayerRuntime extends KinematicBody {
  previousX: number;
  previousY: number;
  runCycle: number;
  grounded: boolean;
  facing: -1 | 1;
  coyoteTimer: number;
  jumpBufferTimer: number;
  invulnerabilityTimer: number;
  animation: PlayerAnimation;
}

interface BlockRuntime extends BlockSpawn {
  used: boolean;
  bumpTimer: number;
}

interface CoinRuntime extends CoinSpawn {
  collected: boolean;
  phase: number;
}

interface EnemyRuntime extends KinematicBody, EnemySpawn {
  previousY: number;
  active: boolean;
  grounded: boolean;
  direction: -1 | 1;
  state: 'alive' | 'squashed' | 'dead';
  squashTimer: number;
}

interface CoinBurst {
  x: number;
  y: number;
  velocityY: number;
  life: number;
  totalLife: number;
}

interface FloatingPopup {
  x: number;
  y: number;
  text: string;
  life: number;
  totalLife: number;
}

const TILE_FRAME: Readonly<Record<TileKind, number>> = {
  'ground-top': 0,
  'ground-fill': 1,
  'brick-platform': 2,
  'stair-block': 3,
};

const GENERATED_GROUND_FRAME = 3;
const SOLID_TILE_BACKING = '#1a0b04';

function makePlayer(x = 0, y = 0): PlayerRuntime {
  return {
    x,
    y,
    previousX: x,
    previousY: y,
    runCycle: 0,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
    velocityX: 0,
    velocityY: 0,
    grounded: false,
    facing: 1,
    coyoteTimer: 0,
    jumpBufferTimer: 0,
    invulnerabilityTimer: 0,
    animation: 'idle',
  };
}

export class PlatformerEngine {
  readonly canvas: HTMLCanvasElement;
  readonly input = new PlatformerInput();
  readonly assetsReady: Promise<AssetLoadReport>;

  private readonly context: CanvasRenderingContext2D;
  private readonly assets: PlatformerAssetBank;
  private readonly listeners = new Set<(event: PlatformerEvent) => void>();
  private readonly pendingEvents: PlatformerEvent[] = [];
  private readonly autoRender: boolean;

  private currentLevel: LevelDefinition = PLATFORMER_LEVELS[0];
  private levelIndex = 0;
  private player = makePlayer();
  private blocks: BlockRuntime[] = [];
  private coinsRuntime: CoinRuntime[] = [];
  private enemies: EnemyRuntime[] = [];
  private solids: SolidCollider[] = [];
  private readonly blockById = new Map<string, BlockRuntime>();
  private readonly coinBursts: CoinBurst[] = [];
  private readonly popups: FloatingPopup[] = [];

  private phase: PlatformerPhase = 'off';
  private powered = false;
  private cartridgeInserted = false;
  private accumulator = 0;
  private simulationTime = 0;
  private fixedStepCount = 0;
  private droppedFrameTime = 0;
  private cameraX = 0;
  private score = 0;
  private coinCount = 0;
  private lives = 3;
  private timerRemaining = 400;
  private timerAccumulator = 0;
  private warningEmitted = false;
  private bootTimer = 0;
  private bootLogoEmitted = false;
  private titleTimer = 0;
  private deathTimer = 0;
  private completionTimer = 0;
  private activeCheckpoint = { x: 32, y: 112, triggerX: 0 };
  private showStatus = false;
  private debugVisible = false;
  private renderDirty = true;
  private textureDirty = true;
  private textureFrameVersion = 0;
  private disposed = false;

  constructor(options: PlatformerEngineOptions = {}) {
    if (options.canvas) {
      this.canvas = options.canvas;
    } else if (typeof document !== 'undefined') {
      this.canvas = document.createElement('canvas');
    } else {
      throw new Error('PlatformerEngine requires a canvas in a browser environment.');
    }

    this.canvas.width = PLATFORMER_SCREEN_WIDTH;
    this.canvas.height = PLATFORMER_SCREEN_HEIGHT;
    this.canvas.dataset.platformerScreen = 'true';
    const context = this.canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Could not create the platformer 2D render context.');
    this.context = context;
    this.context.imageSmoothingEnabled = false;
    this.autoRender = options.autoRender ?? true;

    const manifest = {
      ...DEFAULT_PLATFORMER_ASSET_MANIFEST,
      ...(options.assetManifest ?? {}),
    } as SpriteAssetManifest;
    this.assets = new PlatformerAssetBank(manifest);
    this.assetsReady = this.assets.loadAll().then((report) => {
      this.renderDirty = true;
      if (this.autoRender) this.render();
      return report;
    });

    if (options.onEvent) this.listeners.add(options.onEvent);
    if (options.enableKeyboard !== false && typeof window !== 'undefined') {
      this.input.attachKeyboard(options.keyboardTarget ?? window);
    }

    this.resetCampaignData();
    this.render();
  }

  get screenCanvas(): HTMLCanvasElement {
    return this.canvas;
  }

  get state(): PlatformerPhase {
    return this.phase;
  }

  get isPowered(): boolean {
    return this.powered;
  }

  get hasCartridge(): boolean {
    return this.cartridgeInserted;
  }

  get frameVersion(): number {
    return this.textureFrameVersion;
  }

  setPowered(powered: boolean): void {
    if (this.disposed || this.powered === powered) return;
    this.powered = powered;
    this.input.clearAll();
    this.showStatus = false;
    if (!powered) {
      this.phase = 'off';
      this.accumulator = 0;
      this.emit('power-off');
    } else {
      this.resetCampaignData();
      this.beginBoot();
      this.emit('power-on');
    }
    this.markDirty();
  }

  setCartridgeInserted(inserted: boolean): void {
    if (this.disposed || this.cartridgeInserted === inserted) return;
    this.cartridgeInserted = inserted;
    this.input.clearAll();
    if (inserted) {
      this.emit('cartridge-inserted');
      if (this.powered) {
        this.resetCampaignData();
        this.beginBoot();
      }
    } else {
      this.emit('cartridge-removed');
      if (this.powered) {
        this.phase = 'no-cartridge';
        this.emit('no-cartridge');
      }
    }
    this.markDirty();
  }

  setIntent(intent: PlatformerIntent, pressed: boolean, source = 'physical-console'): void {
    this.input.setIntent(intent, pressed, source);
  }

  pulseIntent(intent: PlatformerIntent, source = 'physical-console-pulse'): void {
    this.input.pulse(intent, source);
  }

  registerAsset(id: SpriteAssetId, definition: SpriteAssetManifest[SpriteAssetId]): void {
    if (id !== definition.id) {
      throw new Error(`Asset id mismatch: expected ${id}, received ${definition.id}.`);
    }
    this.assets.register(definition);
    void this.assets.loadAll().then(() => this.markDirty());
  }

  onEvent(listener: (event: PlatformerEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  drainEvents(): PlatformerEvent[] {
    return this.pendingEvents.splice(0, this.pendingEvents.length);
  }

  setDebugVisible(visible: boolean): void {
    this.debugVisible = visible;
    this.markDirty();
  }

  update(deltaSeconds: number): void {
    if (this.disposed || !Number.isFinite(deltaSeconds) || deltaSeconds <= 0) {
      if (this.autoRender && this.renderDirty) this.render();
      return;
    }

    const acceptedDelta = Math.min(deltaSeconds, MAX_FRAME_DELTA);
    this.droppedFrameTime += Math.max(0, deltaSeconds - acceptedDelta);
    this.accumulator += acceptedDelta;
    let steps = 0;
    while (this.accumulator >= PLATFORMER_FIXED_STEP && steps < MAX_STEPS_PER_UPDATE) {
      this.fixedUpdate(PLATFORMER_FIXED_STEP);
      this.accumulator -= PLATFORMER_FIXED_STEP;
      steps += 1;
    }
    if (steps === MAX_STEPS_PER_UPDATE && this.accumulator >= PLATFORMER_FIXED_STEP) {
      this.droppedFrameTime += this.accumulator;
      this.accumulator = 0;
    }

    if (this.autoRender && this.renderDirty) this.render();
  }

  /** Deterministic stepping helper for interaction tests and scripted QA. */
  advanceFixedSteps(stepCount: number, renderAfter = false): void {
    const count = clamp(Math.floor(stepCount), 0, 60 * 60 * 10);
    for (let step = 0; step < count; step += 1) this.fixedUpdate(PLATFORMER_FIXED_STEP);
    if (renderAfter || this.autoRender) this.render();
  }

  render(): void {
    if (this.disposed) return;
    this.context.imageSmoothingEnabled = false;
    switch (this.phase) {
      case 'off':
        this.renderPoweredOff();
        break;
      case 'boot':
        this.renderBoot();
        break;
      case 'no-cartridge':
        this.renderNoCartridge();
        break;
      case 'title':
        this.renderTitle();
        break;
      default:
        this.renderGame();
        break;
    }
    this.renderDirty = false;
    this.textureDirty = true;
    this.textureFrameVersion += 1;
  }

  consumeRenderDirty(): boolean {
    const dirty = this.renderDirty;
    this.renderDirty = false;
    return dirty;
  }

  /** Returns true once per freshly rendered canvas frame for CanvasTexture uploads. */
  consumeTextureDirty(): boolean {
    const dirty = this.textureDirty;
    this.textureDirty = false;
    return dirty;
  }

  reset(): void {
    this.resetCampaignData();
    if (!this.powered) this.phase = 'off';
    else if (!this.cartridgeInserted) this.phase = 'no-cartridge';
    else this.phase = 'title';
    this.markDirty();
  }

  getDiagnostics(): PlatformerDiagnostics {
    return {
      phase: this.phase,
      powered: this.powered,
      cartridgeInserted: this.cartridgeInserted,
      simulationTime: this.simulationTime,
      fixedStepCount: this.fixedStepCount,
      droppedFrameTime: this.droppedFrameTime,
      levelId: this.currentLevel.id,
      levelIndex: this.levelIndex,
      score: this.score,
      coins: this.coinCount,
      lives: this.lives,
      timer: Math.max(0, Math.ceil(this.timerRemaining)),
      cameraX: this.cameraX,
      player: {
        x: this.player.x,
        y: this.player.y,
        velocityX: this.player.velocityX,
        velocityY: this.player.velocityY,
        grounded: this.player.grounded,
        animation: this.player.animation,
        animationFrame: this.playerAnimationFrame(),
        runCycle: this.player.runCycle,
      },
      entities: {
        enemiesAlive: this.enemies.filter((enemy) => enemy.state !== 'dead').length,
        coinsRemaining: this.coinsRuntime.filter((coin) => !coin.collected).length,
        activeBursts: this.coinBursts.length,
      },
      input: this.input.snapshot(),
    };
  }

  getDebugState(): PlatformerDebugState {
    return {
      level: this.currentLevel,
      diagnostics: this.getDiagnostics(),
      checkpoint: { ...this.activeCheckpoint },
      solids: this.solids.map((solid) => ({ ...solid })),
      blocks: this.blocks.map((blockRuntime) => ({ ...blockRuntime })),
      coins: this.coinsRuntime.map((coinRuntime) => ({ ...coinRuntime })),
      enemies: this.enemies.map((enemy) => ({
        id: enemy.id,
        kind: enemy.kind,
        x: enemy.x,
        y: enemy.y,
        patrolSpeed: enemy.patrolSpeed,
        activationPadding: enemy.activationPadding,
        renderLayer: enemy.renderLayer,
        velocityX: enemy.velocityX,
        velocityY: enemy.velocityY,
        active: enemy.active,
        state: enemy.state,
      })),
      assets: this.assets.getStatus(),
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.input.dispose();
    this.listeners.clear();
    this.pendingEvents.length = 0;
  }

  private fixedUpdate(delta: number): void {
    if (this.disposed) return;
    this.simulationTime += delta;
    this.fixedStepCount += 1;
    this.titleTimer += delta;

    if (this.input.consumePressed('start')) this.handleStartPressed();
    if (this.input.consumePressed('select')) {
      this.showStatus = !this.showStatus;
      this.emit('select', { visible: this.showStatus });
    }

    switch (this.phase) {
      case 'boot':
        this.updateBoot(delta);
        break;
      case 'playing':
        this.updatePlaying(delta);
        break;
      case 'dying':
        this.updateDying(delta);
        break;
      case 'level-complete':
        this.updateLevelComplete(delta);
        break;
      case 'off':
      case 'no-cartridge':
      case 'title':
      case 'paused':
      case 'game-over':
      case 'victory':
        break;
    }
    this.markDirty(false);
  }

  private beginBoot(): void {
    this.phase = 'boot';
    this.bootTimer = 0;
    this.bootLogoEmitted = false;
    this.titleTimer = 0;
  }

  private updateBoot(delta: number): void {
    this.bootTimer += delta;
    if (!this.bootLogoEmitted && this.bootTimer >= 0.62) {
      this.bootLogoEmitted = true;
      this.emit('boot-logo');
    }
    if (this.bootTimer < BOOT_DURATION) return;
    if (this.cartridgeInserted) {
      this.phase = 'title';
      this.titleTimer = 0;
    } else {
      this.phase = 'no-cartridge';
      this.emit('no-cartridge');
    }
  }

  private handleStartPressed(): void {
    switch (this.phase) {
      case 'title':
        this.phase = 'playing';
        this.titleTimer = 0;
        this.emit('game-start', { world: this.currentLevel.worldLabel });
        this.emit('level-start', { world: this.currentLevel.worldLabel });
        break;
      case 'playing':
        this.phase = 'paused';
        this.emit('pause');
        break;
      case 'paused':
        this.phase = 'playing';
        this.emit('resume');
        break;
      case 'level-complete':
        this.completionTimer = Math.max(this.completionTimer, 3.1);
        break;
      case 'game-over':
      case 'victory':
        this.resetCampaignData();
        this.phase = 'playing';
        this.emit('game-start', { world: this.currentLevel.worldLabel });
        this.emit('level-start', { world: this.currentLevel.worldLabel });
        break;
      case 'off':
      case 'boot':
      case 'no-cartridge':
      case 'dying':
        break;
    }
  }

  private updatePlaying(delta: number): void {
    this.updateBlocksAndEffects(delta);
    this.updatePlayer(delta);
    this.updateEnemies(delta);
    this.resolvePlayerEnemyContacts();
    this.collectCoins();
    this.updateCheckpoints();
    this.updateTimer(delta);
    this.updateCamera(delta);

    if (this.player.y > this.currentLevel.collision.pitKillY) {
      this.killPlayer('pit');
      return;
    }
    if (overlaps(this.player, this.currentLevel.sceneHooks.finishZone)) {
      this.completeLevel();
    }
  }

  private updatePlayer(delta: number): void {
    const player = this.player;
    player.previousX = player.x;
    player.previousY = player.y;
    player.invulnerabilityTimer = Math.max(0, player.invulnerabilityTimer - delta);

    if (this.input.consumePressed('jump')) player.jumpBufferTimer = PLAYER_JUMP_BUFFER_TIME;
    else player.jumpBufferTimer = Math.max(0, player.jumpBufferTimer - delta);

    if (player.grounded) player.coyoteTimer = PLAYER_COYOTE_TIME;
    else player.coyoteTimer = Math.max(0, player.coyoteTimer - delta);

    const horizontal = Number(this.input.isHeld('right')) - Number(this.input.isHeld('left'));
    const running = this.input.isHeld('run');
    const maximumSpeed = running ? PLAYER_RUN_SPEED : PLAYER_WALK_SPEED;
    if (horizontal !== 0) {
      player.facing = horizontal < 0 ? -1 : 1;
      const acceleration = player.grounded
        ? running
          ? PLAYER_RUN_ACCELERATION
          : PLAYER_GROUND_ACCELERATION
        : PLAYER_AIR_ACCELERATION;
      const turnBoost = Math.sign(player.velocityX) !== horizontal && Math.abs(player.velocityX) > 4 ? 1.35 : 1;
      player.velocityX = approach(
        player.velocityX,
        horizontal * maximumSpeed,
        acceleration * turnBoost * delta,
      );
    } else {
      const deceleration = player.grounded
        ? PLAYER_GROUND_DECELERATION
        : PLAYER_AIR_DECELERATION;
      player.velocityX = approach(player.velocityX, 0, deceleration * delta);
    }

    if (player.jumpBufferTimer > 0 && player.coyoteTimer > 0) {
      player.velocityY = -(running ? PLAYER_RUN_JUMP_SPEED : PLAYER_JUMP_SPEED);
      player.grounded = false;
      player.coyoteTimer = 0;
      player.jumpBufferTimer = 0;
      this.emit('jump', { running });
    }

    let gravityScale = 1;
    if (player.velocityY < 0) {
      gravityScale = this.input.isHeld('jump') ? 0.55 : 1.65;
    } else if (player.velocityY > 170) {
      gravityScale = 1.12;
    }
    player.velocityY = Math.min(
      PLAYER_MAX_FALL_SPEED,
      player.velocityY + PLAYER_GRAVITY * gravityScale * delta,
    );

    moveHorizontal(player, player.velocityX * delta, this.solids);
    player.x = clamp(
      player.x,
      0,
      this.currentLevel.world.widthTiles * TILE - player.width,
    );
    const traveled = Math.abs(player.x - player.previousX);
    if (player.grounded && traveled > 0.01) {
      player.runCycle = (player.runCycle + traveled / 5.5) % 4;
    } else if (Math.abs(player.velocityX) <= 8) {
      player.runCycle = 0;
    }

    const wasGrounded = player.grounded;
    const verticalVelocity = player.velocityY;
    player.grounded = false;
    const verticalHit = moveVertical(player, player.velocityY * delta, this.solids);
    if (verticalHit.collided && verticalVelocity > 0) {
      player.grounded = true;
      if (!wasGrounded && verticalVelocity > 90) this.emit('land', { speed: verticalVelocity });
    } else if (verticalHit.collided && verticalVelocity < 0 && verticalHit.collider?.kind === 'block') {
      this.hitBlock(verticalHit.collider.id);
      player.velocityY = 24;
    }

    player.animation = this.choosePlayerAnimation();
  }

  private updateEnemies(delta: number): void {
    for (const enemy of this.enemies) {
      if (enemy.state === 'dead') continue;
      if (!enemy.active) {
        const activationEdge = this.cameraX + PLATFORMER_SCREEN_WIDTH + enemy.activationPadding;
        if (enemy.x <= activationEdge) enemy.active = true;
        else continue;
      }
      if (enemy.state === 'squashed') {
        enemy.squashTimer -= delta;
        if (enemy.squashTimer <= 0) enemy.state = 'dead';
        continue;
      }

      enemy.previousY = enemy.y;
      enemy.velocityX = enemy.direction * enemy.patrolSpeed;
      enemy.velocityY = Math.min(280, enemy.velocityY + PLAYER_GRAVITY * delta);
      const horizontalHit = moveHorizontal(enemy, enemy.velocityX * delta, this.solids);
      if (horizontalHit.collided) {
        enemy.direction = enemy.direction === 1 ? -1 : 1;
        enemy.velocityX = enemy.direction * enemy.patrolSpeed;
      }
      enemy.grounded = false;
      const verticalHit = moveVertical(enemy, enemy.velocityY * delta, this.solids);
      if (verticalHit.collided && enemy.velocityY === 0) enemy.grounded = true;
      if (enemy.y > this.currentLevel.collision.pitKillY + 24) enemy.state = 'dead';
    }
  }

  private resolvePlayerEnemyContacts(): void {
    if (this.phase !== 'playing' || this.player.invulnerabilityTimer > 0) return;
    for (const enemy of this.enemies) {
      if (enemy.state !== 'alive' || !overlaps(this.player, enemy)) continue;
      const previousBottom = this.player.previousY + this.player.height;
      const descending = this.player.velocityY > 20;
      if (descending && previousBottom <= enemy.y + 5) {
        enemy.state = 'squashed';
        enemy.squashTimer = 0.42;
        enemy.velocityX = 0;
        enemy.velocityY = 0;
        this.player.y = enemy.y - this.player.height;
        this.player.velocityY = -168;
        this.score += 100;
        this.popups.push({
          x: enemy.x,
          y: enemy.y - 4,
          text: '100',
          life: 0.72,
          totalLife: 0.72,
        });
        this.emit('stomp', { enemy: enemy.id, score: 100 });
      } else {
        this.killPlayer('enemy');
        return;
      }
    }
  }

  private collectCoins(): void {
    for (const coin of this.coinsRuntime) {
      if (coin.collected) continue;
      const bounds = { x: coin.x, y: coin.y, width: 8, height: 12 };
      if (!overlaps(this.player, bounds)) continue;
      coin.collected = true;
      this.awardCoin(coin.x, coin.y, 100);
    }
  }

  private awardCoin(x: number, y: number, points: number): void {
    this.coinCount += 1;
    this.score += points;
    if (this.coinCount % 100 === 0) this.lives += 1;
    this.coinBursts.push({ x, y, velocityY: -54, life: 0.48, totalLife: 0.48 });
    this.popups.push({ x, y: y - 4, text: `${points}`, life: 0.72, totalLife: 0.72 });
    this.emit('coin', { coins: this.coinCount, score: points, lives: this.lives });
  }

  private hitBlock(blockId: string): void {
    const blockRuntime = this.blockById.get(blockId);
    if (!blockRuntime || blockRuntime.bumpTimer > 0) return;
    blockRuntime.bumpTimer = 0.18;
    this.emit('block-hit', { id: blockId, kind: blockRuntime.kind });
    if (blockRuntime.kind === 'question' && !blockRuntime.used) {
      blockRuntime.used = true;
      if (blockRuntime.contains === 'coin') {
        this.coinCount += 1;
        this.score += 200;
        if (this.coinCount % 100 === 0) this.lives += 1;
        this.coinBursts.push({
          x: blockRuntime.x + 4,
          y: blockRuntime.y - 2,
          velocityY: -145,
          life: 0.72,
          totalLife: 0.72,
        });
        this.popups.push({
          x: blockRuntime.x + 2,
          y: blockRuntime.y - 8,
          text: '200',
          life: 0.8,
          totalLife: 0.8,
        });
        this.emit('coin', { coins: this.coinCount, score: 200, source: 'question-block' });
      }
    }
  }

  private updateBlocksAndEffects(delta: number): void {
    for (const blockRuntime of this.blocks) {
      blockRuntime.bumpTimer = Math.max(0, blockRuntime.bumpTimer - delta);
    }
    for (let index = this.coinBursts.length - 1; index >= 0; index -= 1) {
      const burst = this.coinBursts[index];
      burst.life -= delta;
      burst.velocityY += 420 * delta;
      burst.y += burst.velocityY * delta;
      if (burst.life <= 0) this.coinBursts.splice(index, 1);
    }
    for (let index = this.popups.length - 1; index >= 0; index -= 1) {
      const popup = this.popups[index];
      popup.life -= delta;
      popup.y -= 18 * delta;
      if (popup.life <= 0) this.popups.splice(index, 1);
    }
  }

  private updateTimer(delta: number): void {
    this.timerAccumulator += delta;
    while (this.timerAccumulator >= 1 && this.phase === 'playing') {
      this.timerAccumulator -= 1;
      this.timerRemaining = Math.max(0, this.timerRemaining - 1);
      if (!this.warningEmitted && this.timerRemaining <= 100) {
        this.warningEmitted = true;
        this.emit('time-warning', { time: this.timerRemaining });
      }
      if (this.timerRemaining <= 0) {
        this.killPlayer('time');
        return;
      }
    }
  }

  private updateCheckpoints(): void {
    for (const checkpoint of this.currentLevel.sceneHooks.checkpoints) {
      if (checkpoint.triggerX <= this.activeCheckpoint.triggerX) continue;
      if (this.player.x < checkpoint.triggerX) continue;
      this.activeCheckpoint = {
        x: checkpoint.spawnX,
        y: checkpoint.spawnY,
        triggerX: checkpoint.triggerX,
      };
    }
  }

  private updateCamera(delta: number): void {
    const worldWidth = this.currentLevel.world.widthTiles * TILE;
    const maximumCameraX = Math.max(0, worldWidth - PLATFORMER_SCREEN_WIDTH);
    const lookAhead = clamp(this.player.velocityX * 0.23, -18, 30);
    const target = clamp(this.player.x - 82 + lookAhead, 0, maximumCameraX);
    const responsiveness = 1 - Math.exp(-delta * 8.5);
    this.cameraX += (target - this.cameraX) * responsiveness;
    if (Math.abs(this.cameraX - target) < 0.02) this.cameraX = target;
  }

  private killPlayer(reason: 'enemy' | 'pit' | 'time'): void {
    if (this.phase !== 'playing') return;
    this.phase = 'dying';
    this.deathTimer = 0;
    this.player.animation = 'death';
    this.player.velocityX = 0;
    this.player.velocityY = -224;
    this.player.grounded = false;
    this.emit('damage', { reason });
    this.emit('death', { reason, lives: this.lives });
  }

  private updateDying(delta: number): void {
    this.deathTimer += delta;
    this.player.previousY = this.player.y;
    this.player.velocityY += 590 * delta;
    this.player.y += this.player.velocityY * delta;
    this.updateBlocksAndEffects(delta);
    if (this.deathTimer < 1.65) return;

    this.lives -= 1;
    if (this.lives <= 0) {
      this.phase = 'game-over';
      this.emit('game-over', { score: this.score });
      return;
    }

    const respawn = { ...this.activeCheckpoint };
    this.loadLevel(this.levelIndex);
    this.activeCheckpoint = respawn;
    this.placePlayer(respawn.x, respawn.y, 1.15);
    this.cameraX = clamp(respawn.x - 64, 0, this.maximumCameraX());
    this.phase = 'playing';
    this.emit('respawn', { lives: this.lives, x: respawn.x });
  }

  private completeLevel(): void {
    if (this.phase !== 'playing') return;
    this.phase = 'level-complete';
    this.completionTimer = 0;
    const timeBonus = Math.max(0, Math.ceil(this.timerRemaining)) * 10;
    this.score += timeBonus;
    this.player.velocityX = 0;
    this.emit('level-complete', {
      world: this.currentLevel.worldLabel,
      timeBonus,
      score: this.score,
    });
  }

  private updateLevelComplete(delta: number): void {
    this.completionTimer += delta;
    this.updateBlocksAndEffects(delta);
    if (this.completionTimer > 0.42 && this.completionTimer < 2.7) {
      this.player.velocityX = 42;
      this.player.velocityY = Math.min(PLAYER_MAX_FALL_SPEED, this.player.velocityY + PLAYER_GRAVITY * delta);
      const previousX = this.player.x;
      moveHorizontal(this.player, this.player.velocityX * delta, this.solids);
      this.player.runCycle = (this.player.runCycle + Math.abs(this.player.x - previousX) / 5.5) % 4;
      const verticalHit = moveVertical(this.player, this.player.velocityY * delta, this.solids);
      this.player.grounded = verticalHit.collided && this.player.velocityY === 0;
      this.player.animation = this.player.grounded ? 'run' : this.choosePlayerAnimation();
      this.updateCamera(delta);
    }
    if (this.completionTimer < 3.05) return;

    if (this.levelIndex + 1 < PLATFORMER_LEVELS.length) {
      this.levelIndex += 1;
      this.loadLevel(this.levelIndex);
      this.phase = 'playing';
      this.emit('level-start', { world: this.currentLevel.worldLabel });
    } else {
      this.phase = 'victory';
      this.emit('victory', { score: this.score, coins: this.coinCount });
    }
  }

  private resetCampaignData(): void {
    this.levelIndex = 0;
    this.score = 0;
    this.coinCount = 0;
    this.lives = 3;
    this.loadLevel(0);
    this.accumulator = 0;
    this.showStatus = false;
  }

  private loadLevel(index: number): void {
    const definition = PLATFORMER_LEVELS[index];
    if (!definition) throw new Error(`Missing platformer level at index ${index}.`);
    this.currentLevel = definition;
    this.blocks = definition.layers.blocks.map((spawn) => ({
      ...spawn,
      used: false,
      bumpTimer: 0,
    }));
    this.blockById.clear();
    this.blocks.forEach((blockRuntime) => this.blockById.set(blockRuntime.id, blockRuntime));
    this.coinsRuntime = definition.layers.coins.map((spawn, coinIndex) => ({
      ...spawn,
      collected: false,
      phase: coinIndex * 0.17,
    }));
    this.enemies = definition.layers.enemies.map((spawn) => ({
      ...spawn,
      previousY: spawn.y,
      width: 16,
      height: 14,
      velocityX: -spawn.patrolSpeed,
      velocityY: 0,
      active: false,
      grounded: false,
      direction: -1,
      state: 'alive',
      squashTimer: 0,
    }));
    this.solids = this.buildSolidColliders(definition);
    this.coinBursts.length = 0;
    this.popups.length = 0;
    this.timerRemaining = definition.world.timeLimit;
    this.timerAccumulator = 0;
    this.warningEmitted = false;
    this.cameraX = 0;
    this.activeCheckpoint = {
      x: definition.sceneHooks.playerSpawn.x,
      y: definition.sceneHooks.playerSpawn.y,
      triggerX: 0,
    };
    this.placePlayer(this.activeCheckpoint.x, this.activeCheckpoint.y, 0.65);
  }

  private buildSolidColliders(definition: LevelDefinition): SolidCollider[] {
    const colliders: SolidCollider[] = [];
    for (const run of definition.layers.tileRuns) {
      colliders.push({
        id: run.id,
        kind: 'tile',
        x: run.x * TILE,
        y: run.y * TILE,
        width: run.length * TILE,
        height: TILE,
      });
    }
    for (const blockRuntime of definition.layers.blocks) {
      colliders.push({
        id: blockRuntime.id,
        kind: 'block',
        x: blockRuntime.x,
        y: blockRuntime.y,
        width: TILE,
        height: TILE,
      });
    }
    for (const pipeSpawn of definition.layers.pipes) {
      colliders.push({
        id: pipeSpawn.id,
        kind: 'pipe',
        x: pipeSpawn.x,
        y: pipeSpawn.y,
        width: pipeSpawn.width,
        height: pipeSpawn.height,
      });
    }
    return colliders;
  }

  private placePlayer(x: number, y: number, invulnerability: number): void {
    this.player = makePlayer(x, y);
    this.player.invulnerabilityTimer = invulnerability;
  }

  private choosePlayerAnimation(): PlayerAnimation {
    if (this.phase === 'dying') return 'death';
    if (!this.player.grounded) return this.player.velocityY < 45 ? 'jump' : 'fall';
    if (Math.abs(this.player.velocityX) > 8) return 'run';
    return 'idle';
  }

  private maximumCameraX(): number {
    return Math.max(
      0,
      this.currentLevel.world.widthTiles * TILE - PLATFORMER_SCREEN_WIDTH,
    );
  }

  private renderPoweredOff(): void {
    const context = this.context;
    context.fillStyle = '#1a211d';
    context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);
    context.fillStyle = 'rgba(111, 128, 116, 0.12)';
    for (let y = 0; y < PLATFORMER_SCREEN_HEIGHT; y += 3) {
      context.fillRect(0, y, PLATFORMER_SCREEN_WIDTH, 1);
    }
  }

  private renderBoot(): void {
    const context = this.context;
    const fade = clamp(this.bootTimer / 0.3, 0, 1);
    const light = Math.floor(34 + fade * 210);
    context.fillStyle = `rgb(${light}, ${light + 3}, ${light})`;
    context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);
    const dropProgress = clamp((this.bootTimer - 0.22) / 0.62, 0, 1);
    const eased = 1 - (1 - dropProgress) * (1 - dropProgress) * (1 - dropProgress);
    const logoY = Math.round(-22 + eased * 82);
    if (dropProgress > 0) {
      drawPixelText(context, 'GAME BOY', 120, logoY, {
        scale: 3,
        color: '#243048',
        align: 'center',
      });
      drawPixelText(context, 'ADVANCE', 120, logoY + 26, {
        scale: 1,
        color: '#5b4aa8',
        align: 'center',
      });
    }
    if (this.bootTimer > 1.05) {
      drawPixelText(context, 'READY', 120, 132, {
        scale: 1,
        color: '#58615b',
        align: 'center',
      });
    }
  }

  private renderNoCartridge(): void {
    const context = this.context;
    context.fillStyle = '#d4d8cf';
    context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);
    context.fillStyle = '#1f2824';
    context.fillRect(27, 37, 186, 84);
    context.fillStyle = '#e7eadf';
    context.fillRect(30, 40, 180, 78);
    drawPixelText(context, 'NO GAME CARTRIDGE', 120, 55, {
      scale: 1,
      color: '#222a25',
      align: 'center',
    });
    drawPixelText(context, 'DETECTED', 120, 70, {
      scale: 2,
      color: '#8f2533',
      align: 'center',
    });
    drawPixelText(context, 'INSERT CARTRIDGE', 120, 101, {
      scale: 1,
      color: '#3d4a42',
      align: 'center',
    });
  }

  private renderTitle(): void {
    this.renderWorld(false);
    const context = this.context;
    context.fillStyle = 'rgba(12, 36, 105, 0.86)';
    context.fillRect(18, 27, 204, 89);
    context.fillStyle = '#f8d800';
    context.fillRect(21, 30, 198, 3);
    context.fillRect(21, 110, 198, 3);
    drawPixelText(context, 'SUPER', 120, 39, {
      scale: 2,
      color: '#ffffff',
      shadowColor: '#000000',
      align: 'center',
    });
    drawPixelText(context, 'MARIO', 120, 58, {
      scale: 3,
      color: '#f04428',
      shadowColor: '#321400',
      align: 'center',
    });
    drawPixelText(context, 'ADVANCE', 120, 85, {
      scale: 2,
      color: '#f8d800',
      shadowColor: '#6b2700',
      align: 'center',
    });
    if (Math.floor(this.titleTimer * 2.2) % 2 === 0) {
      drawPixelText(context, 'PRESS START', 120, 124, {
        scale: 1,
        color: '#ffffff',
        shadowColor: '#000000',
        align: 'center',
      });
    }
    drawPixelText(context, `WORLD ${this.currentLevel.worldLabel}`, 120, 143, {
      scale: 1,
      color: '#ffffff',
      shadowColor: '#000000',
      align: 'center',
    });
  }

  private renderGame(): void {
    this.renderWorld(true);
    const context = this.context;
    if (this.phase === 'paused') {
      context.fillStyle = 'rgba(0, 0, 0, 0.58)';
      context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);
      drawPixelText(context, 'PAUSED', 120, 65, {
        scale: 2,
        color: '#ffffff',
        shadowColor: '#000000',
        align: 'center',
      });
      drawPixelText(context, 'PRESS START', 120, 88, {
        color: '#f8d800',
        shadowColor: '#000000',
        align: 'center',
      });
    } else if (this.phase === 'level-complete') {
      context.fillStyle = 'rgba(0, 0, 0, 0.35)';
      context.fillRect(25, 55, 190, 38);
      drawPixelText(context, 'LEVEL CLEAR!', 120, 64, {
        scale: 2,
        color: '#ffffff',
        shadowColor: '#000000',
        align: 'center',
      });
    } else if (this.phase === 'game-over') {
      context.fillStyle = 'rgba(0, 0, 0, 0.72)';
      context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);
      drawPixelText(context, 'GAME OVER', 120, 59, {
        scale: 2,
        color: '#ffffff',
        shadowColor: '#000000',
        align: 'center',
      });
      drawPixelText(context, 'PRESS START', 120, 86, {
        color: '#f8d800',
        align: 'center',
      });
    } else if (this.phase === 'victory') {
      context.fillStyle = 'rgba(0, 0, 80, 0.74)';
      context.fillRect(11, 38, 218, 80);
      drawPixelText(context, 'YOU WIN!', 120, 49, {
        scale: 2,
        color: '#f8d800',
        shadowColor: '#000000',
        align: 'center',
      });
      drawPixelText(context, `SCORE ${this.score.toString().padStart(6, '0')}`, 120, 78, {
        color: '#ffffff',
        align: 'center',
      });
      drawPixelText(context, 'START TO REPLAY', 120, 99, {
        color: '#ffffff',
        align: 'center',
      });
    }

    if (this.showStatus) this.renderStatusOverlay();
  }

  private renderWorld(includeHud: boolean): void {
    const context = this.context;
    context.fillStyle = this.currentLevel.world.backgroundColor;
    context.fillRect(0, 0, PLATFORMER_SCREEN_WIDTH, PLATFORMER_SCREEN_HEIGHT);

    const generatedLayers = this.renderGeneratedBackgroundLayers();
    if (!generatedLayers.sky) this.renderScenery('far-bg');
    if (!generatedLayers.far) this.renderScenery('mid-bg');
    this.renderScenery('near-bg', generatedLayers.near);
    this.renderTileRuns();
    this.renderBlocks();
    this.renderPipes();
    this.renderCoins();
    this.renderEnemies();
    this.renderEffects();
    this.renderPlayer();
    if (this.debugVisible) this.renderDebugCollision();
    if (includeHud) this.renderHud();
  }

  private renderGeneratedBackgroundLayers(): { sky: boolean; far: boolean; near: boolean } {
    // The reference uses a flat NES palette and discrete outlined scenery. Keep
    // generated plates as audited source material, but prefer the authored sprite
    // composition at runtime for sharper, more coherent pixels.
    const useGeneratedPlates = false;
    if (!useGeneratedPlates) return { sky: false, far: false, near: false };
    const sky = this.drawNamedBackgroundLayer('sky');
    const far = this.drawNamedBackgroundLayer('far-bg');
    const near = this.drawNamedBackgroundLayer('near-bg');
    return { sky, far, near };
  }

  private drawNamedBackgroundLayer(id: 'sky' | 'far-bg' | 'near-bg'): boolean {
    const layer = this.currentLevel.parallaxLayers.find((candidate) => candidate.id === id);
    if (!layer) return false;
    return this.drawRepeatedBackgroundLayer(layer.assetId, layer.scrollFactor);
  }

  private drawRepeatedBackgroundLayer(
    assetId: 'sky-layer' | 'far-bg-layer' | 'near-bg-layer',
    parallax: number,
  ): boolean {
    if (!this.assets.isReady(assetId)) return false;
    const offset = -((this.cameraX * parallax) % PLATFORMER_SCREEN_WIDTH);
    this.assets.drawFrame(
      this.context,
      assetId,
      0,
      offset,
      0,
      PLATFORMER_SCREEN_WIDTH,
      PLATFORMER_SCREEN_HEIGHT,
    );
    this.assets.drawFrame(
      this.context,
      assetId,
      0,
      offset + PLATFORMER_SCREEN_WIDTH,
      0,
      PLATFORMER_SCREEN_WIDTH,
      PLATFORMER_SCREEN_HEIGHT,
    );
    return true;
  }

  private renderScenery(
    layer: 'far-bg' | 'mid-bg' | 'near-bg',
    landmarksOnly = false,
  ): void {
    const context = this.context;
    for (const object of this.currentLevel.layers.scenery) {
      if (object.renderLayer !== layer) continue;
      if (landmarksOnly && object.kind !== 'castle' && object.kind !== 'flagpole') continue;
      const screenX = (object.x - this.cameraX) * object.parallax;
      const dimensions = this.sceneryDimensions(object.kind, object.variant);
      if (screenX + dimensions.width < -4 || screenX > PLATFORMER_SCREEN_WIDTH + 4) continue;
      const assetId = object.kind as SpriteAssetId;
      const drawn = this.assets.drawFrame(
        context,
        assetId,
        object.variant,
        screenX,
        object.y,
        dimensions.width,
        dimensions.height,
      );
      if (!drawn) {
        drawFallbackScenery(context, object.kind, screenX, object.y, object.variant);
      }
    }
  }

  private sceneryDimensions(kind: 'cloud' | 'hill' | 'bush' | 'castle' | 'flagpole', variant: number): { width: number; height: number } {
    switch (kind) {
      case 'cloud':
        return { width: 42 + variant * 8, height: 18 };
      case 'hill':
        return { width: 42 + variant * 12, height: 30 };
      case 'bush':
        return { width: 42 + variant * 14, height: 21 };
      case 'castle':
        return { width: 64, height: 48 };
      case 'flagpole':
        return { width: 24, height: 96 };
    }
  }

  private renderTileRuns(): void {
    for (const run of this.currentLevel.layers.tileRuns) {
      if (run.tile === 'ground-fill' && this.assets.isReady('ground-middle')) continue;
      if (run.tile === 'ground-top' && this.assets.isReady('ground-middle')) {
        this.renderGeneratedGroundRun(run.x, run.y, run.length);
        continue;
      }
      const firstVisible = Math.max(
        0,
        Math.floor((this.cameraX - run.x * TILE) / TILE) - 1,
      );
      const lastVisible = Math.min(
        run.length - 1,
        Math.ceil((this.cameraX + PLATFORMER_SCREEN_WIDTH - run.x * TILE) / TILE) + 1,
      );
      for (let index = firstVisible; index <= lastVisible; index += 1) {
        const screenX = (run.x + index) * TILE - this.cameraX;
        const screenY = run.y * TILE;
        const individualAsset =
          run.tile === 'brick-platform'
            ? 'brick-block'
            : run.tile === 'stair-block'
              ? 'brick-block'
              : null;
        if (individualAsset !== null) {
          this.context.fillStyle = SOLID_TILE_BACKING;
          this.context.fillRect(Math.round(screenX), Math.round(screenY), TILE, TILE);
        }
        const drawn = individualAsset
          ? this.assets.drawFrame(
              this.context,
              individualAsset,
              0,
              screenX,
              screenY,
              TILE,
              TILE,
            )
          : this.assets.drawFrame(
              this.context,
              'tileset',
              TILE_FRAME[run.tile],
              screenX,
              screenY,
              TILE,
              TILE,
            );
        if (!drawn) drawFallbackTile(this.context, run.tile, screenX, screenY);
      }
    }
  }

  private renderGeneratedGroundRun(xTile: number, yTile: number, lengthTiles: number): void {
    const runX = xTile * TILE;
    const runWidth = lengthTiles * TILE;
    const groundY = yTile * TILE;
    const firstVisibleTile = Math.max(0, Math.floor((this.cameraX - runX) / TILE) - 1);
    const lastVisibleTile = Math.min(
      lengthTiles - 1,
      Math.ceil((this.cameraX + PLATFORMER_SCREEN_WIDTH - runX) / TILE) + 1,
    );

    // Generated ground strips intentionally contain transparent padding around
    // their artwork. Build the complete two-tile collision footprint first so
    // those pixels reveal continuous terrain instead of the blue sky.
    for (let tileIndex = firstVisibleTile; tileIndex <= lastVisibleTile; tileIndex += 1) {
      const screenX = Math.round(runX + tileIndex * TILE - this.cameraX);
      for (let row = 0; row < 2; row += 1) {
        const screenY = groundY + row * TILE;
        this.context.fillStyle = SOLID_TILE_BACKING;
        this.context.fillRect(screenX, screenY, TILE, TILE);
        const drawn = this.assets.drawFrame(
          this.context,
          'tileset',
          GENERATED_GROUND_FRAME,
          screenX,
          screenY,
          TILE,
          TILE,
        );
        if (!drawn) drawFallbackTile(this.context, row === 0 ? 'ground-top' : 'ground-fill', screenX, screenY);
      }
    }

    const segmentWidth = 64;
    const segmentCount = Math.ceil(runWidth / segmentWidth);
    for (let segment = 0; segment < segmentCount; segment += 1) {
      const worldX = runX + segment * segmentWidth;
      const drawWidth = Math.min(segmentWidth, runX + runWidth - worldX);
      const screenX = Math.round(worldX - this.cameraX);
      if (screenX + drawWidth < 0 || screenX > PLATFORMER_SCREEN_WIDTH) continue;
      const assetId =
        segment === 0
          ? 'ground-left'
          : segment === segmentCount - 1
            ? 'ground-right'
            : 'ground-middle';
      const transparentTop = assetId === 'ground-left' ? 5 : 4;
      const drawn = this.assets.drawFrame(
        this.context,
        assetId,
        0,
        screenX,
        groundY - transparentTop,
        drawWidth,
        24,
      );
      if (!drawn) {
        const tileCount = Math.ceil(drawWidth / TILE);
        for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
          drawFallbackTile(
            this.context,
            'ground-top',
            screenX + tileIndex * TILE,
            groundY,
          );
        }
      }
    }
  }

  private renderBlocks(): void {
    const animationFrame = Math.floor(this.simulationTime * 4) % 2;
    for (const blockRuntime of this.blocks) {
      const screenX = blockRuntime.x - this.cameraX;
      if (screenX < -TILE || screenX > PLATFORMER_SCREEN_WIDTH) continue;
      const bumpProgress = blockRuntime.bumpTimer > 0 ? 1 - blockRuntime.bumpTimer / 0.18 : 0;
      const offsetY = blockRuntime.bumpTimer > 0 ? -Math.sin(bumpProgress * Math.PI) * 5 : 0;
      const kind = blockRuntime.used ? 'used' : blockRuntime.kind;
      const tileFrame = kind === 'brick' ? 4 : kind === 'question' ? 5 : 6;
      const individualAsset =
        kind === 'brick' ? 'brick-block' : kind === 'question' ? 'question-block' : 'used-block';
      this.context.fillStyle = SOLID_TILE_BACKING;
      this.context.fillRect(
        Math.round(screenX),
        Math.round(blockRuntime.y + offsetY),
        TILE,
        TILE,
      );
      const drawn =
        this.assets.drawFrame(
          this.context,
          individualAsset,
          0,
          screenX,
          blockRuntime.y + offsetY,
          TILE,
          TILE,
        ) ||
        this.assets.drawFrame(
          this.context,
          'tileset',
          tileFrame,
          screenX,
          blockRuntime.y + offsetY,
          TILE,
          TILE,
        );
      if (!drawn) {
        drawFallbackBlock(
          this.context,
          kind,
          screenX,
          blockRuntime.y + offsetY,
          animationFrame,
        );
      }
    }
  }

  private renderPipes(): void {
    for (const pipeSpawn of this.currentLevel.layers.pipes) {
      const screenX = pipeSpawn.x - this.cameraX;
      if (screenX + pipeSpawn.width + 4 < 0 || screenX - 4 > PLATFORMER_SCREEN_WIDTH) continue;
      const generatedPipe = this.assets.drawFrame(
        this.context,
        'pipe',
        0,
        screenX,
        pipeSpawn.y,
        pipeSpawn.width,
        pipeSpawn.height,
      );
      if (generatedPipe) continue;
      if (!this.assets.isReady('tileset')) {
        drawFallbackPipe(
          this.context,
          screenX,
          pipeSpawn.y,
          pipeSpawn.width,
          pipeSpawn.height,
        );
        continue;
      }
      for (let row = 0; row < pipeSpawn.height / TILE; row += 1) {
        const leftFrame = row === 0 ? 8 : 10;
        const rightFrame = row === 0 ? 9 : 11;
        this.assets.drawFrame(
          this.context,
          'tileset',
          leftFrame,
          screenX - (row === 0 ? 3 : 0),
          pipeSpawn.y + row * TILE,
          TILE + (row === 0 ? 3 : 0),
          TILE,
        );
        this.assets.drawFrame(
          this.context,
          'tileset',
          rightFrame,
          screenX + TILE,
          pipeSpawn.y + row * TILE,
          TILE + (row === 0 ? 3 : 0),
          TILE,
        );
      }
    }
  }

  private renderCoins(): void {
    for (const coinRuntime of this.coinsRuntime) {
      if (coinRuntime.collected) continue;
      const screenX = coinRuntime.x - this.cameraX;
      if (screenX < -10 || screenX > PLATFORMER_SCREEN_WIDTH + 2) continue;
      const frame = Math.floor((this.simulationTime + coinRuntime.phase) * 10) % 4;
      const coinWidth = frame % 4 === 1 || frame % 4 === 3 ? 3 : frame % 4 === 2 ? 6 : 8;
      const drawn = this.assets.drawFrame(
        this.context,
        'coin',
        frame,
        screenX + (8 - coinWidth) / 2,
        coinRuntime.y,
        coinWidth,
        12,
      );
      if (!drawn) drawFallbackCoin(this.context, screenX, coinRuntime.y, frame);
    }
  }

  private renderEnemies(): void {
    for (const enemy of this.enemies) {
      if (enemy.state === 'dead' || !enemy.active) continue;
      const screenX = enemy.x - this.cameraX;
      if (screenX < -20 || screenX > PLATFORMER_SCREEN_WIDTH + 4) continue;
      const squashed = enemy.state === 'squashed';
      const frame = squashed ? 1 : Math.floor(this.simulationTime * 7) % 2;
      const drawn = this.assets.drawFrame(
        this.context,
        squashed ? 'goomba-squashed' : 'goomba-walk',
        frame,
        screenX,
        enemy.y + (squashed ? 6 : 0),
        16,
        squashed ? 8 : 14,
        enemy.direction > 0,
      );
      if (!drawn) {
        drawFallbackGoomba(this.context, screenX, enemy.y, frame, squashed);
      }
    }
  }

  private renderEffects(): void {
    for (const burst of this.coinBursts) {
      const screenX = burst.x - this.cameraX;
      const frame = Math.floor((1 - burst.life / burst.totalLife) * 4);
      const drawn = this.assets.drawFrame(
        this.context,
        'coin',
        frame,
        screenX,
        burst.y,
        8,
        12,
      );
      if (!drawn) drawFallbackCoin(this.context, screenX, burst.y, frame);
    }
    for (const popup of this.popups) {
      const alpha = clamp(popup.life / (popup.totalLife * 0.35), 0, 1);
      this.context.save();
      this.context.globalAlpha = alpha;
      drawPixelText(this.context, popup.text, popup.x - this.cameraX, popup.y, {
        color: '#ffffff',
        shadowColor: '#000000',
        align: 'center',
      });
      this.context.restore();
    }
  }

  private renderPlayer(): void {
    if (
      this.player.invulnerabilityTimer > 0 &&
      Math.floor(this.player.invulnerabilityTimer * 16) % 2 === 0 &&
      this.phase !== 'dying'
    ) {
      return;
    }
    const screenX = this.player.x - this.cameraX;
    const frame = this.playerAnimationFrame();
    const assetId = `mario-${this.player.animation}` as SpriteAssetId;
    const drawn = this.assets.drawFrame(
      this.context,
      assetId,
      frame,
      screenX - 1,
      this.player.y - 8,
      16,
      24,
      this.player.facing < 0,
    );
    if (!drawn) {
      drawFallbackMario(
        this.context,
        screenX,
        this.player.y,
        this.player.facing,
        this.player.animation,
        frame,
      );
    }
  }

  private playerAnimationFrame(): number {
    switch (this.player.animation) {
      case 'idle':
        return Math.floor(this.simulationTime * 3) % 4;
      case 'run':
        return Math.floor(this.player.runCycle) % 4;
      case 'jump':
        return this.player.velocityY < -130 ? 0 : this.player.velocityY < -40 ? 1 : 2;
      case 'fall':
        return this.player.velocityY < 150 ? 0 : 1;
      case 'death':
        return Math.min(3, Math.floor(this.deathTimer * 6));
    }
  }

  private renderHud(): void {
    const context = this.context;
    const scoreText = this.score.toString().padStart(6, '0');
    const timerText = Math.max(0, Math.ceil(this.timerRemaining)).toString().padStart(3, '0');
    drawPixelText(context, 'MARIO', 5, 3, { color: '#ffffff', shadowColor: '#000000' });
    drawPixelText(context, scoreText, 5, 12, { color: '#ffffff', shadowColor: '#000000' });
    drawFallbackCoin(context, 66, 10, Math.floor(this.simulationTime * 8));
    drawPixelText(context, `X${this.coinCount.toString().padStart(2, '0')}`, 76, 12, {
      color: '#ffffff',
      shadowColor: '#000000',
    });
    drawPixelText(context, 'LIVES', 98, 3, { color: '#ffffff', shadowColor: '#000000' });
    drawPixelText(context, `X${this.lives}`, 106, 12, { color: '#ffffff', shadowColor: '#000000' });
    drawPixelText(context, 'WORLD', 142, 3, { color: '#ffffff', shadowColor: '#000000' });
    drawPixelText(context, this.currentLevel.worldLabel, 151, 12, {
      color: '#ffffff',
      shadowColor: '#000000',
    });
    drawPixelText(context, 'TIME', 207, 3, { color: '#ffffff', shadowColor: '#000000' });
    drawPixelText(context, timerText, 211, 12, { color: '#ffffff', shadowColor: '#000000' });
  }

  private renderStatusOverlay(): void {
    const context = this.context;
    context.fillStyle = 'rgba(0, 0, 0, 0.82)';
    context.fillRect(20, 37, 200, 78);
    context.strokeStyle = '#f8d800';
    context.strokeRect(21.5, 38.5, 197, 75);
    drawPixelText(context, 'CONTROLS', 120, 47, {
      scale: 2,
      color: '#f8d800',
      align: 'center',
    });
    drawPixelText(context, 'DPAD OR ARROWS MOVE', 120, 69, {
      color: '#ffffff',
      align: 'center',
    });
    drawPixelText(context, 'A Z SPACE JUMP', 120, 82, {
      color: '#ffffff',
      align: 'center',
    });
    drawPixelText(context, 'B X SHIFT RUN', 120, 95, {
      color: '#ffffff',
      align: 'center',
    });
  }

  private renderDebugCollision(): void {
    const context = this.context;
    context.save();
    context.strokeStyle = '#ff00ff';
    context.globalAlpha = 0.72;
    for (const solid of this.solids) {
      const screenX = solid.x - this.cameraX;
      if (screenX + solid.width < 0 || screenX > PLATFORMER_SCREEN_WIDTH) continue;
      context.strokeRect(
        Math.round(screenX) + 0.5,
        Math.round(solid.y) + 0.5,
        Math.round(solid.width) - 1,
        Math.round(solid.height) - 1,
      );
    }
    context.strokeStyle = '#00ffff';
    context.strokeRect(
      Math.round(this.player.x - this.cameraX) + 0.5,
      Math.round(this.player.y) + 0.5,
      this.player.width - 1,
      this.player.height - 1,
    );
    context.restore();
  }

  private emit(
    type: PlatformerEventType,
    data?: Record<string, number | string | boolean>,
  ): void {
    const event: PlatformerEvent = {
      type,
      simulationTime: this.simulationTime,
      ...(data ? { data } : {}),
    };
    this.pendingEvents.push(event);
    if (this.pendingEvents.length > 128) this.pendingEvents.shift();
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch {
        // External audio/UI hooks must not interrupt deterministic simulation.
      }
    }
  }

  private markDirty(renderImmediately = true): void {
    this.renderDirty = true;
    if (renderImmediately && this.autoRender) this.render();
  }
}
